/**
 * Day 2 main page — two-pane layout.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ HeaderBanner (dev-tool disclaimer + version)                 │
 *   ├──────────────┬───────────────────────────────────────────────┤
 *   │ EndpointList │ EndpointForm                                  │
 *   │  (left rail) │ ResponseViewer                                │
 *   │              │                                               │
 *   └──────────────┴───────────────────────────────────────────────┘
 *
 * The currently-selected endpoint id is persisted in the URL hash so
 * F5 / link-sharing preserves it. Default selection on cold start =
 * first endpoint in the catalog (ops_health).
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchCatalog } from '@/lib/api';
import type { EndpointSpec, ProxyResult } from '@/lib/types';
import { EndpointList } from '@/components/EndpointList';
import { EndpointForm } from '@/components/EndpointForm';
import { ResponseViewer } from '@/components/ResponseViewer';

function hashSelection(): string | null {
  const raw = window.location.hash.replace(/^#/, '');
  return raw === '' ? null : raw;
}

export function EndpointExplorer() {
  const [catalog, setCatalog] = useState<EndpointSpec[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(hashSelection());
  const [results, setResults] = useState<Record<string, ProxyResult>>({});

  useEffect(() => {
    let cancelled = false;
    void fetchCatalog()
      .then((c) => {
        if (cancelled) return;
        setCatalog(c.endpoints);
        if (selectedId === null && c.endpoints.length > 0) {
          const first = c.endpoints[0];
          if (first !== undefined) {
            setSelectedId(first.id);
          }
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
    // intentionally one-shot — catalog is static across the session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId !== null) {
      window.location.hash = selectedId;
    }
  }, [selectedId]);

  const selected = useMemo<EndpointSpec | null>(() => {
    if (catalog === null || selectedId === null) return null;
    return catalog.find((e) => e.id === selectedId) ?? null;
  }, [catalog, selectedId]);

  if (loadErr !== null) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-900">
          <strong className="block mb-1">Failed to load endpoint catalog.</strong>
          <code className="font-mono text-xs">{loadErr}</code>
          <p className="mt-2 text-xs">
            Check that the Fastify backend is running:{' '}
            <code className="font-mono">pnpm --filter @nikira/op-emulator dev:server</code>
          </p>
        </div>
      </div>
    );
  }

  if (catalog === null) {
    return <div className="p-8 text-sm text-slate-500">Loading endpoint catalog…</div>;
  }

  return (
    <div className="grid grid-cols-[280px_1fr] h-[calc(100vh-92px)] min-h-0">
      <aside className="border-r border-slate-200 bg-white min-h-0">
        <EndpointList endpoints={catalog} selectedId={selectedId} onSelect={setSelectedId} />
      </aside>
      <main className="overflow-y-auto p-6 space-y-6 bg-slate-50">
        {selected ? (
          <>
            <EndpointForm
              endpoint={selected}
              onResult={(r) => setResults((prev) => ({ ...prev, [selected.id]: r }))}
            />
            <ResponseViewer result={results[selected.id] ?? null} />
          </>
        ) : (
          <p className="text-sm text-slate-500">Select an endpoint from the left.</p>
        )}
      </main>
    </div>
  );
}
